import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { usePageDepth } from './PageDepthContext';

export interface PageHeaderProps {
  /** Page title — the page's only `h1` (an `h2` when the page is shown inside another page) */
  title?: ReactNode;
  /**
   * Counters and badges beside the title. They sit outside the `h1`, so the
   * heading's accessible name stays the page name instead of changing with
   * every filter.
   */
  titleAside?: ReactNode;
  /** Optional subtitle / description under the title */
  subtitle?: ReactNode;
  /** Optional icon beside the title (same as the sidebar icon) */
  icon?: LucideIcon;
  /** Breadcrumb row above the title; the first crumb is «Aelo» linking to "/" */
  breadcrumbs?: ReactNode;
  /** Controls on the right of the header */
  actions?: ReactNode;
  /**
   * Pins the header to the top of the app shell's scroll region while the
   * list under it scrolls. At rest it sits where the static header does: it
   * takes over `PageBody`'s `p-6` (so it assumes that padding and being the
   * first child) and paints the shell background (`bg-bg-void`) so rows scroll
   * under it. It layers above in-content sticky rows (`z-header`); a sticky
   * table header under it needs a `top` offset of the header's height.
   */
  sticky?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * The header of every page (#2073, program #2079): breadcrumbs, icon, `h1`
 * `text-lg font-medium`, subtitle `text-sm text-fg-muted`, actions on the
 * right. Render it as the first child of `PageBody`.
 */
export function PageHeader({
  title,
  titleAside,
  subtitle,
  icon: Icon,
  breadcrumbs,
  actions,
  sticky = false,
  className,
  'data-testid': testId = 'page-header',
}: PageHeaderProps) {
  // A page shown inside another page (the admin viewer renders whole role
  // dashboards) is a section of it: the outer page keeps the h1 and the
  // breadcrumb trail, the inner one draws an h2 and no trail of its own.
  const nested = usePageDepth() > 1;
  const headingClass = 'text-lg font-medium text-fg tracking-tight';
  const heading = title ? (
    nested ? (
      <h2 className={headingClass}>{title}</h2>
    ) : (
      <h1 className={headingClass}>{title}</h1>
    )
  ) : null;
  return (
    <div
      data-testid={testId}
      className={cn(
        'flex items-center justify-between gap-4',
        sticky ? 'sticky top-0 z-header -mx-6 -mt-6 bg-bg-void px-6 pt-6 pb-4' : 'mb-4',
        className
      )}
    >
      <div className="min-w-0">
        {breadcrumbs && !nested ? <div className="mb-1">{breadcrumbs}</div> : null}
        {title || Icon || subtitle ? (
          <div className="flex items-center gap-3">
            {Icon ? <Icon className="h-6 w-6 shrink-0 text-primary" /> : null}
            <div className="min-w-0">
              {heading && titleAside ? (
                <div className="flex items-center gap-3">
                  {heading}
                  {titleAside}
                </div>
              ) : (
                heading
              )}
              {subtitle ? <p className="text-sm text-fg-muted">{subtitle}</p> : null}
            </div>
          </div>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-3">{actions}</div> : null}
    </div>
  );
}
